import {
    ApolloClient,
    ApolloLink,
    DocumentNode,
    HttpLink,
    InMemoryCache,
    OperationVariables,
    TypedDocumentNode,
} from "@apollo/client";

import { getStrapiBaseUrl } from "../strapiUtils";

// Get Strapi GraphQL endpoint, automatically detecting hostname in browser
const getStrapiUrl = (): string => {
    const baseUrl = getStrapiBaseUrl();
    const graphqlUrl = `${baseUrl}/graphql`;

    // Debug logging in development
/*    if (import.meta.env.DEV) {
        console.log("[getStrapiUrl] GraphQL endpoint:", graphqlUrl);
    }*/

    return graphqlUrl;
};

// Create HTTP link for GraphQL requests
const httpLink = new HttpLink({
    uri: getStrapiUrl(),
    // Use native fetch (available in Node.js 18+ and browsers)
    // For older Node.js versions, you may need to install and use node-fetch
    ...(typeof window === "undefined" && typeof globalThis.fetch === "undefined"
        ? { fetch: require("node-fetch") }
        : {}),
});

// Optional: Create auth link for adding authentication headers
const authLink = new ApolloLink((operation, forward) => {
    // Add any auth headers here if needed
    // Example: const token = getAuthToken();
    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            // Uncomment and modify if you need auth:
            // Authorization: token ? `Bearer ${token}` : '',
        },
    }));
    return forward(operation);
});

// Create the Apollo Client instance
export const client = new ApolloClient({
    link: ApolloLink.from([authLink, httpLink]),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    messages: {
                        merge(existing, incoming) {
                            return incoming;
                        },
                    },
                    phases: {
                        merge(existing, incoming) {
                            return incoming;
                        },
                    },
                },
            },
        },
    }),
    ssrMode: typeof window === "undefined",
    defaultOptions: {
        watchQuery: {
            errorPolicy: "all",
            fetchPolicy: "cache-and-network",
        },
        query: {
            errorPolicy: "all",
            fetchPolicy: "network-only",
        },
    },
});

/**
 * Fetch data from Strapi GraphQL API
 * @param query - GraphQL query document
 * @param variables - Optional query variables
 * @returns Promise resolving to the query data
 * @throws Error if the query fails
 */
export const fetchFromStrapi = async <TData = any, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    variables?: TVariables,
): Promise<TData> => {
    try {
        const queryOptions: any = {
            query,
            fetchPolicy: "network-only", // Always fetch fresh data for SSR
        };

        if (variables) {
            queryOptions.variables = variables;
        }

        const result = await client.query<TData, TVariables>(queryOptions);

        // Check for GraphQL errors (from networkError or graphQLErrors)
        if (result.error) {
            throw result.error;
        }

        if (!result.data) {
            throw new Error("No data returned from GraphQL query");
        }

        return result.data as TData;
    } catch (error) {
        // Enhanced error logging
        if (error instanceof Error) {
            console.error(`[fetchFromStrapi] Error fetching data:`, {
                message: error.message,
                query: query?.loc?.source?.body,
                variables,
            });
            throw error;
        }
        throw new Error(`Unknown error occurred: ${String(error)}`);
    }
};
